import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useTheme } from 'styled-components';

import Button from '~components/Button';
import Pencil from '~components/Icon/Pencil';
import { COMPANY_DETAIL_MENU } from '~constant/company';
import useGetCompanyInfo from '~hooks/query/company/useCompanyInfo';
import useGetInterview from '~hooks/query/review/useGetInterview';
import useGetMyInfoQuery from '~hooks/query/user/useMyInfoQuery';
import { useRouterQuery } from '~hooks/useRouterQuery';
import { RegisterRef } from '~types/company';
import { setGtmDataLayer } from '~utils/common/thirdPartyEvent';
import { isClient } from '~utils/lodash/isServer';

import Card from '../Card';
import ChartContents from './ChartContents';
import { useDisplayTrigger } from './hooks';
import NudgeLogin from './NudgeLogin';
import * as S from './style';

interface Props {
  registerRef: RegisterRef;
}

export default function InterviewReviewInfo({ registerRef }: Props) {
  const { isActiveUser, data: userInfo } = useGetMyInfoQuery();
  const router = useRouter();
  const theme = useTheme();
  const companyHash = useRouterQuery('companyId');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isDisplay = useDisplayTrigger(wrapperRef);
  const { data: companyInfo } = useGetCompanyInfo(companyHash);
  const { data, isLoading } = useGetInterview(companyHash);

  // useEffect(() => {
  //   setGtmDataLayer('')
  // }, [])

  useEffect(() => {
    const [path, hash] = router.asPath.split('#');

    if (wrapperRef.current && hash === 'company-review-chart' && isClient) {
      router.replace(path);
      wrapperRef.current.scrollIntoView();
    }
  }, [router.asPath]);

  return (
    <Card
      title='면접 리뷰'
      sectionId={COMPANY_DETAIL_MENU.INTERVIEW_REVIEW}
      isNew
      registerRef={registerRef}
      rightCardHeader={
        <>
          {isActiveUser && userInfo?.interviewReview && (
            <S.RightCardHeaderWrapper>
              {!isLoading && data?.totalCount === 0 && (
                <S.Tooltip animStart={isDisplay}>
                  기업의 첫 리뷰를 <S.BreakLineOnlyMobile />
                  작성해주세요!
                </S.Tooltip>
              )}
              <Link
                href={`/review/interview?companyHash=${companyHash}&beforeUrl=/company/${companyHash}#interview-review-chart`}>
                <Button
                  data-attribute-id='company__interviewReview__write__click'
                  data-is-blur='false'
                  data-company-hash={companyInfo?.PK_NM_HASH}
                  data-company-name={companyInfo?.name}
                  variant={!isLoading && data?.totalCount === 0 ? 'magenta' : 'light'}
                  size='sm'
                  as='a'
                  leading={
                    <S.ButtonWriteWrapper>
                      <Pencil
                        color={
                          !isLoading && data?.totalCount === 0
                            ? theme.colors.white100
                            : theme.colors.gray800
                        }
                        width={12}
                        height={12}
                      />
                    </S.ButtonWriteWrapper>
                  }>
                  리뷰 작성
                </Button>
              </Link>
            </S.RightCardHeaderWrapper>
          )}
        </>
      }>
      <S.Contents ref={wrapperRef}>
        {isActiveUser && userInfo?.interviewReview ? <ChartContents /> : <NudgeLogin />}
      </S.Contents>
    </Card>
  );
}
